import { Authentication } from '@leedpulse-monorepo/classes';
import { EBillingInterval } from '@leedpulse-monorepo/enums';
import Failure from '../../../core/Failure';
import HttpClient from '../../../core/HttpClient';

// ─── Get Stripe subscription info for a company ──────────────────────────────

export interface SubscriptionInfo {
  has_subscription: boolean;
  /** Whether the Stripe customer has at least one valid payment method */
  has_valid_payment: boolean;
  stripe_subscription_id?: string;
  /** Unix timestamp — start of current billing period */
  current_period_start?: number;
  /** Unix timestamp — end of current billing period (= next billing date) */
  current_period_end?: number;
  interval?: EBillingInterval | null; // Read live from Stripe, authoritative over the mirror
  upcoming_invoice?: {
    /** Amount in cents */
    amount_due: number;
    currency: string;
  };
}

/**
 * Fetch the Stripe subscription info for a company.
 * Returns whether a subscription exists, billing period dates, and upcoming invoice.
 */
export async function getSubscriptionInfo(
  companyId: string,
  companyApiKey: string,
  auth: Authentication,
): Promise<SubscriptionInfo> {
  try {
    const response = await HttpClient.get(
      `/leedflow/stripe/subscription/info?company_id=${encodeURIComponent(companyId)}`,
      auth.token,
      {
        'x-api-key': companyApiKey,
        'x-user-uid': auth.id,
      },
    );
    return response.data;
  } catch (e) {
    console.error('getSubscriptionInfo error:', e);
    if (e instanceof TypeError && e.message === 'Failed to fetch') {
      throw new GetSubscriptionInfoFailure('Le serveur ne répond pas');
    } else if (e instanceof Error) {
      throw new GetSubscriptionInfoFailure(e.message);
    }
    return Promise.reject(e);
  }
}

// ─── Failure class ────────────────────────────────────────────────────────────

export class GetSubscriptionInfoFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}
